"use client";

import React, { useMemo, useState } from "react";
import { type VariantProps } from "class-variance-authority";
import { v4 as uuid } from "uuid";
import type { TagVariantType } from "./Tag";
import { toast } from "./hooks/useToast";
import { TagList } from "./TagList";
import { Autocomplete } from "./Autocomplete";
import { Input } from "./Input";
import { Button } from "./Button";
import { arrayify } from "@repo/shared/utils";

enum Delimiter {
  Comma = ",",
  Enter = "Enter",
  Space = " ",
}

type OmittedInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size" | "value"
>;

export type Tag = {
  id: string;
  text: string;
};

export interface TagInputProps
  extends OmittedInputProps,
    VariantProps<TagVariantType> {
  placeholder?: string;
  tags: Tag[];
  setTags: React.Dispatch<React.SetStateAction<Tag[]>>;
  enableAutocomplete?: boolean;
  autocompleteOptions?: Tag[];
  autocompleteAddOnPick?: boolean;
  maxTags?: number;
  readOnly?: boolean;
  disabled?: boolean;
  onTagAdd?: (tag: string) => void;
  onTagRemove?: (tag: string) => void;
  allowDuplicates?: boolean;
  validateTag?: (tag: string) => boolean;
  delimiter?: Delimiter | string[];
  showCount?: boolean;
  placeholderWhenFull?: string;
  sortTags?: boolean;
  truncate?: number;
  minLength?: number;
  maxLength?: number;
  value?: string | number | readonly string[] | { id: string; text: string }[];
  autocompleteFilter?: (option: string) => boolean;
  direction?: "row" | "column";
  onInputChange?: (value: string) => void;
  customTagRenderer?: (tag: Tag) => React.ReactNode;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onTagClick?: (tag: Tag) => void;
  draggable?: boolean;
  inputFieldPostion?: "bottom" | "top" | "inline";
  clearAll?: boolean;
  onClearAll?: () => void;
  addOnBlur?: boolean;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

export const TagInput = Object.assign(
  React.forwardRef<HTMLInputElement, TagInputProps>(
    (
      {
        id,
        placeholder,
        tags,
        setTags,
        variant,
        size,
        shape,
        className,
        enableAutocomplete,
        autocompleteOptions,
        autocompleteAddOnPick = false,
        maxTags,
        delimiter,
        onTagAdd,
        onTagRemove,
        allowDuplicates,
        showCount,
        validateTag,
        placeholderWhenFull = "Max tags reached",
        sortTags,
        truncate,
        autocompleteFilter,
        borderStyle,
        textCase,
        interaction,
        animation,
        textStyle,
        minLength,
        maxLength,
        direction = "row",
        onInputChange,
        customTagRenderer,
        onFocus,
        onBlur,
        onTagClick,
        draggable = false,
        inputFieldPostion = "bottom",
        clearAll = false,
        onClearAll,
        addOnBlur,
        inputProps = {},
        disabled,
      },
      inputRef
    ) => {
      tags = tags.map((tag) =>
        typeof tag === "string" ? { text: tag, id: "" } : tag
      );

      maxTags ??= Infinity;
      if (maxTags < 0) maxTags = 0;

      delimiter = arrayify(delimiter ?? [Delimiter.Comma, Delimiter.Enter]);

      const [inputValue, setInputValue] = useState("");
      const [tagCount, setTagCount] = useState(Math.max(0, tags.length));
      const [draggedTagId, setDraggedTagId] = useState<string | null>(null);

      const addTag = (newTagText: string) => {
        // validate tag
        if (validateTag && !validateTag(newTagText)) {
          return;
        }

        if (minLength && newTagText.length < minLength) {
          toast({
            title: "Tag is too short",
            description: "Please enter a tag with more characters",
            variant: "destructive",
          });
          return;
        }

        if (maxLength && newTagText.length > maxLength) {
          toast({
            title: "Tag is too long",
            description: "Please enter a tag with less characters",
            variant: "destructive",
          });
          return;
        }

        // create / add tag
        const newTagId = uuid();

        if (
          newTagText &&
          (allowDuplicates || !tags.some((tag) => tag.text === newTagText)) &&
          tags.length < maxTags
        ) {
          setTags([...tags, { id: newTagId, text: newTagText }]);
          onTagAdd?.(newTagText);
          setTagCount((prevTagCount) => prevTagCount + 1);
        }
      };

      const addTagFromInput = () => {
        addTag(inputValue.trim());
        setInputValue("");
      };

      const handleInputChange = (
        arg: React.ChangeEvent<HTMLInputElement> | string
      ) => {
        const newValue = typeof arg === "string" ? arg : arg.target.value;
        setInputValue(newValue);
        onInputChange?.(newValue);
      };

      const handleKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
        if (delimiter.includes(evt.key)) {
          evt.preventDefault();
          addTagFromInput();
        }
      };

      const removeTag = (idToRemove: string) => {
        setTags(tags.filter((tag) => tag.id !== idToRemove));
        onTagRemove?.(tags.find((tag) => tag.id === idToRemove)?.text || "");
        setTagCount((prevTagCount) => prevTagCount - 1);
      };

      const handleDragStart = (id: string) => {
        setDraggedTagId(id);
      };

      const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault(); // Necessary to allow dropping
      };

      const handleDrop = (id: string) => {
        if (draggedTagId === null) return;

        const draggedTagIndex = tags.findIndex(
          (tag) => tag.id === draggedTagId
        );
        const dropTargetIndex = tags.findIndex((tag) => tag.id === id);

        if (draggedTagIndex === dropTargetIndex) return;

        const newTags = [...tags];
        const [reorderedTag] = newTags.splice(draggedTagIndex, 1);
        newTags.splice(dropTargetIndex, 0, reorderedTag);

        setTags(newTags);
        setDraggedTagId(null);
      };

      const handleClearAll = () => {
        onClearAll?.();
      };

      const handleFocus = (evt: React.FocusEvent<HTMLInputElement>) => {
        onFocus?.(evt);
      };

      const handleBlur = (evt: React.FocusEvent<HTMLInputElement>) => {
        onBlur?.(evt);
        if (!enableAutocomplete && addOnBlur) addTagFromInput();
      };

      const filteredAutocompleteOptions = useMemo(() => {
        if (!autocompleteOptions) return [];

        let options = autocompleteOptions;

        if (!allowDuplicates) {
          const used = new Set(tags.map((tag) => tag.text));
          options = options.filter((option) => !used.has(option.text));
        }
        if (autocompleteFilter) {
          options = options.filter((option) => autocompleteFilter(option.text));
        }

        return options;
      }, [autocompleteOptions, autocompleteFilter, allowDuplicates, tags]);

      const displayedTags = sortTags ? [...tags].sort() : tags;

      const truncatedTags = truncate
        ? tags.map((tag) => ({
            id: tag.id,
            text:
              tag.text?.length > truncate
                ? `${tag.text.substring(0, truncate)}...`
                : tag.text,
          }))
        : displayedTags;

      const inputPlaceholder =
        maxTags !== undefined && tags.length >= maxTags
          ? placeholderWhenFull
          : placeholder;

      let inputJSX = (
        <Input
          ref={inputRef}
          id={id ?? uuid()}
          type="text"
          placeholder={inputPlaceholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...inputProps}
          className={className}
          disabled={
            disabled || (maxTags !== undefined && tags.length >= maxTags)
          }
        />
      );

      if (enableAutocomplete) {
        inputJSX = (
          <Autocomplete
            autocompleteOptions={filteredAutocompleteOptions}
            onSelect={(value) => {
              if (autocompleteAddOnPick) {
                addTag(value);
                setInputValue("");
              } else {
                setInputValue(value);
              }
            }}
            filterText={inputValue}
            onBlur={() => {
              if (addOnBlur) addTagFromInput();
            }}
          >
            {inputJSX}
          </Autocomplete>
        );
      }

      return (
        <div
          className={`w-full flex gap-3 ${
            inputFieldPostion === "bottom"
              ? "flex-col"
              : inputFieldPostion === "top"
                ? "flex-col-reverse"
                : "flex-row"
          }`}
        >
          <TagList
            tags={truncatedTags}
            customTagRenderer={customTagRenderer}
            variant={variant}
            size={size}
            shape={shape}
            borderStyle={borderStyle}
            textCase={textCase}
            interaction={interaction}
            animation={animation}
            textStyle={textStyle}
            onTagClick={onTagClick}
            draggable={draggable}
            handleDragStart={handleDragStart}
            handleDragOver={handleDragOver}
            handleDrop={handleDrop}
            onRemoveTag={removeTag}
            direction={direction}
            disabled={disabled}
          />

          {!disabled && <div className="w-full">{inputJSX}</div>}

          {showCount && maxTags && (
            <div className="flex">
              <span className="text-muted-foreground text-sm mt-1 ml-auto">
                {`${tagCount}`}/{`${maxTags}`}
              </span>
            </div>
          )}
          {clearAll && (
            <Button type="button" onClick={handleClearAll} className="mt-2">
              Clear All
            </Button>
          )}
        </div>
      );
    }
  ),
  { Delimiter }
);

TagInput.displayName = "TagInput";
